import { trashApi as api } from './trashApi';
export const addTagTypes = ['Trashes'] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes
  })
  .injectEndpoints({
    endpoints: build => ({
      getTrash: build.query<GetTrashApiResponse, GetTrashApiArg>({
        query: queryArg => ({
          url: `/trash`,
          params: {
            search: queryArg.search,
            offset: queryArg.offset,
            limit: queryArg.limit,
            sortby: queryArg.sortby,
            orderby: queryArg.orderby,
            userId: queryArg.userId
          }
        }),
        providesTags: ['Trashes']
      }),
      getInactiveItemsCounts: build.query<
        GetInactiveItemsCountsApiResponse,
        GetInactiveItemsCountsApiArg
      >({
        query: queryArg => ({
          url: `/trashCount`,
          params: {
            subscriberId: queryArg.subscriberId,
            userId: queryArg.userId
          }
        }),
        providesTags: ['Trashes']
      }),
      getTrashStats: build.query<GetTrashStatsApiResponse, GetTrashStatsApiArg>(
        {
          query: queryArg => ({
            url: `/trash/stats`,
            params: {
              subscriberId: queryArg.subscriberId,
              userId: queryArg.userId
            }
          }),
          providesTags: ['Trashes']
        }
      ),
      checkAssetUsage: build.query<
        CheckAssetUsageApiResponse,
        CheckAssetUsageApiArg
      >({
        query: queryArg => ({ url: `/trash/check-usage/${queryArg.assetId}` }),
        providesTags: ['Trashes']
      }),
      removeAssetFromModule: build.mutation<
        RemoveAssetFromModuleApiResponse,
        RemoveAssetFromModuleApiArg
      >({
        query: queryArg => ({
          url: `/trash/deleteByModule`,
          method: 'POST',
          body: queryArg.createTrashModule
        }),
        invalidatesTags: ['Trashes']
      }),
      permanentlyDeleteAsset: build.mutation<
        PermanentlyDeleteAssetApiResponse,
        PermanentlyDeleteAssetApiArg
      >({
        query: queryArg => ({
          url: `/trash/permanent/${queryArg.assetId}`,
          method: 'DELETE'
        }),
        invalidatesTags: ['Trashes']
      }),
      restoreAsset: build.mutation<RestoreAssetApiResponse, RestoreAssetApiArg>(
        {
          query: queryArg => ({
            url: `/trash/restore/${queryArg.assetId}`,
            method: 'POST'
          }),
          invalidatesTags: ['Trashes']
        }
      ),
      bulkRestoreAssets: build.mutation<
        BulkRestoreAssetsApiResponse,
        BulkRestoreAssetsApiArg
      >({
        query: queryArg => ({
          url: `/trashRestore`,
          method: 'POST',
          body: queryArg.bulkRestoreTrash
        }),
        invalidatesTags: ['Trashes']
      }),
      hardDeleteOldInactiveAssets: build.mutation<
        HardDeleteOldInactiveAssetsApiResponse,
        HardDeleteOldInactiveAssetsApiArg
      >({
        query: queryArg => ({
          url: `/trashCron`,
          method: 'DELETE',
          params: {
            subscriberId: queryArg.subscriberId,
            userId: queryArg.userId
          }
        }),
        invalidatesTags: ['Trashes']
      }),
      bulkTrashOperation: build.mutation<
        BulkTrashOperationApiResponse,
        BulkTrashOperationApiArg
      >({
        query: queryArg => ({
          url: `/trash/bulkOperation`,
          method: 'POST',
          body: queryArg.bulkOperation
        }),
        invalidatesTags: ['Trashes']
      })
    }),
    overrideExisting: false
  });
export { injectedRtkApi as enhancedApi };
export type GetTrashApiResponse = /** status 200 Trashes found. */ {
  ok?: boolean;
  data?: Trash;
  message?: string;
};
export type GetTrashApiArg = {
  /** Search text */
  search?: string;
  /** offset for pagination */
  offset?: number;
  /** number of rows to fetch */
  limit?: number;
  /** sort */
  sortby?: string;
  /** order */
  orderby?: string;
  /** userId */
  userId?: string;
};
export type GetInactiveItemsCountsApiResponse =
  /** status 200 Trashes found. */ {
    ok?: boolean;
    data?: TrashCount;
    message?: string;
  };
export type GetInactiveItemsCountsApiArg = {
  /** Subscriber ID  */
  subscriberId?: string;
  /** User ID */
  userId?: string;
};
export type GetTrashStatsApiResponse =
  /** status 200 Trash statistics retrieved successfully */ void;
export type GetTrashStatsApiArg = {
  /** Subscriber ID */
  subscriberId?: string;
  /** User ID */
  userId?: string;
};
export type CheckAssetUsageApiResponse = /** status 200 Trashes found. */ {
  ok?: boolean;
  data?: Trash;
  message?: string;
};
export type CheckAssetUsageApiArg = {
  /** Asset ID to check */
  assetId: string;
};
export type RemoveAssetFromModuleApiResponse =
  /** status 200 Asset associations removed successfully */ {
    ok?: boolean;
    data?: {
      /** Total number of asset associations removed */
      deletedCount?: number;
      /** Unique array of asset IDs that had their associations removed */
      deletedAssetIds?: string[];
      /** Detailed breakdown by module */
      details?: {
        module?: string;
        deletedCount?: number;
        assetIds?: string[];
        message?: string;
      }[];
    };
    message?: string;
  };
export type RemoveAssetFromModuleApiArg = {
  /** Array of modules with their moduleIds to find and remove deleted asset associations */
  createTrashModule: CreateTrashModule;
};
export type PermanentlyDeleteAssetApiResponse =
  /** status 200 Asset permanently deleted successfully */ void;
export type PermanentlyDeleteAssetApiArg = {
  /** Asset ID to permanently delete */
  assetId: string;
};
export type RestoreAssetApiResponse =
  /** status 200 Asset restored successfully */ void;
export type RestoreAssetApiArg = {
  /** Asset ID to restore */
  assetId: string;
};
export type BulkRestoreAssetsApiResponse =
  /** status 200 Bulk restore completed */ {
    ok?: boolean;
    data?: {
      /** Number of assets successfully restored */
      success?: number;
      /** Number of assets that failed to restore */
      failed?: number;
      /** Array of restored asset IDs */
      restoredAssets?: string[];
    };
    message?: string;
  };
export type BulkRestoreAssetsApiArg = {
  /** Array of modules with their moduleIds to find and remove deleted asset associations */
  bulkRestoreTrash: BulkRestoreTrash;
};
export type HardDeleteOldInactiveAssetsApiResponse =
  /** status 200 Hard delete completed successfully */ {
    ok?: boolean;
    data?: {
      /** Number of assets permanently deleted */
      deletedCount?: number;
      /** Array of deleted asset IDs */
      deletedAssets?: string[];
      /** Array of errors encountered during deletion */
      errors?: {
        assetId?: string;
        error?: string;
      }[];
    };
    message?: string;
  };
export type HardDeleteOldInactiveAssetsApiArg = {
  /** Filter by Subscriber ID */
  subscriberId?: string;
  /** Filter by User ID */
  userId?: string;
};
export type BulkTrashOperationApiResponse =
  /** status 200 Bulk operation completed successfully */ {
    ok?: boolean;
    data?: {
      /** Operation success status */
      success?: boolean;
      /** Operation performed (restore/delete) */
      operation?: string;
      /** Number of assets affected */
      assetsAffected?: number;
      /** Number of recipes affected */
      recipesAffected?: number;
      /** Number of moments affected */
      momentsAffected?: number;
      /** Array of errors encountered during operation */
      errors?: {
        /** Item type (asset/recipe/moment) */
        type?: string;
        /** Item ID */
        id?: string;
        /** Error message */
        error?: string;
      }[];
    };
    message?: string;
  };
export type BulkTrashOperationApiArg = {
  /** Array of modules with their moduleIds to find and remove deleted asset associations */
  bulkOperation: BulkOperation;
};
export type Trash = {
  count?: number;
  rows?: {
    id?: string;
    subscriberId?: string;
    userId?: string;
    entityType?: string;
    url?: string;
    thumbNailUrl?: string;
    type?: string;
    contentType?: string;
    status?: string;
    createdAt?: string;
    updatedAt?: string;
  }[];
  summary?: {
    assets?: number;
    recipes?: number;
    moments?: number;
  };
};
export type TrashCount = {
  subscriberId?: string;
  userId?: string;
  counts?: {
    assets?: number;
    recipes?: number;
    moments?: number;
    memories?: number;
    people?: number;
    folder?: number;
  };
};
export type CreateTrashModule = {
  modules?: {
    module?: string;
    moduleIds?: string[];
    folderIds?: string[];
  }[];
};
export type BulkRestoreTrash = {
  assetIds?: any[];
  module?: string;
  moduleId?: string[];
  folderIds?: string[];
};
export type BulkOperation = {
  flag?: string;
};
export const {
  useGetTrashQuery,
  useGetInactiveItemsCountsQuery,
  useGetTrashStatsQuery,
  useCheckAssetUsageQuery,
  useRemoveAssetFromModuleMutation,
  usePermanentlyDeleteAssetMutation,
  useRestoreAssetMutation,
  useBulkRestoreAssetsMutation,
  useHardDeleteOldInactiveAssetsMutation,
  useBulkTrashOperationMutation
} = injectedRtkApi;
